import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, Shield, KeyRound, Ban, Trash2, Terminal, RefreshCcw, Copy, ExternalLink, Download, X } from "lucide-react";
import { api, startImpersonation } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Slide-over showing per-account EA setup for the admin.  */
function EASetupSlideOver({ account, onClose, onRegenerated }) {
  const [showKey, setShowKey] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  if (!account) return null;
  const eaKey = account.ea_api_key || "";
  const masked = eaKey ? `${eaKey.slice(0, 8)}${"•".repeat(Math.max(0, eaKey.length - 12))}${eaKey.slice(-4)}` : "—";

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const regenerate = async () => {
    if (!window.confirm("Regenerate EA API key? The old key will stop working immediately — the user's EA must be updated with the new key.")) return;
    setRegenBusy(true);
    try {
      const r = await api.post(`/accounts/${account.id}/regenerate-key`);
      toast.success("EA key regenerated");
      onRegenerated?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
    finally { setRegenBusy(false); }
  };

  const downloadEA = async (version) => {
    try {
      const r = await api.get(`/ea/download${version ? "?version=" + version : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `veerox_ea${version ? "_v" + version.replace(".", "_") : ""}.mq5`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("EA downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ea-setup-slideover">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-[#111827] border-l border-[#1f2937] p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="vx-label">EA Setup · {account.account_name}</div>
            <div className="text-xs font-mono text-[#6b7280] mt-1 uppercase">{account.broker_id} · {account.currency}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#1f2937] text-[#9ca3af]"
                  data-testid="close-ea-setup">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Backend URL */}
        <div className="mb-4">
          <div className="vx-label mb-1">Backend URL</div>
          <div className="flex gap-1">
            <Input readOnly value={BACKEND_URL} className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs"
                   data-testid="ea-backend-url" />
            <Button variant="outline" size="sm" onClick={() => copy(BACKEND_URL)}
                    className="border-[#1f2937] hover:bg-[#1f2937] h-9">
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* EA API Key */}
        <div className="mb-4">
          <div className="vx-label mb-1">EA API Key</div>
          <div className="flex gap-1">
            <Input readOnly value={showKey ? eaKey : masked}
                   className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs"
                   data-testid="ea-api-key" />
            <Button variant="outline" size="sm" onClick={() => setShowKey((s) => !s)}
                    className="border-[#1f2937] hover:bg-[#1f2937] h-9">
              {showKey ? "Hide" : "Show"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => copy(eaKey)}
                    className="border-[#1f2937] hover:bg-[#1f2937] h-9">
              <Copy className="w-3 h-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={regenerate} disabled={regenBusy}
                    className="border-[#fbbf24]/40 text-[#fbbf24] hover:bg-[#fbbf24]/10 h-9"
                    data-testid="regenerate-ea-key-btn">
              {regenBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        {/* Download */}
        <div className="mb-4">
          <div className="vx-label mb-1">MQL5 EA download</div>
          <div className="flex gap-1">
            <Button size="sm" onClick={() => downloadEA()}
                    className="bg-[#3b82f6] hover:bg-[#2563eb] text-white text-xs h-8"
                    data-testid="download-ea-latest">
              <Download className="w-3 h-3 mr-1" /> Latest (v1.50)
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadEA("1.40")}
                    className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8">
              v1.40
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadEA("1.30")}
                    className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8">
              v1.30
            </Button>
          </div>
        </div>

        {/* Setup steps */}
        <div className="rounded border border-[#1f2937] p-3 bg-[#0a0e1a]/50">
          <div className="vx-label mb-2">5-step setup</div>
          <ol className="list-decimal list-inside space-y-1.5 text-xs text-[#9ca3af]">
            <li>Download <code className="text-[#e5e7eb]">veerox_ea_v1_50.mq5</code></li>
            <li>Open MetaEditor (F4) → place file in <code className="text-[#e5e7eb]">MQL5/Experts/</code> → compile (F7)</li>
            <li>MT5 → Tools → Options → Expert Advisors → allow WebRequest for
              <br /><code className="text-[#3b82f6] break-all">{BACKEND_URL}</code></li>
            <li>Drag EA onto chart → in Inputs paste API key + Backend URL</li>
            <li>Enable AutoTrading (Ctrl+E) → check Experts tab for connection log</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [eaSetupAcct, setEaSetupAcct] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/users/${user_id}`)
      .then((r) => {
        setData(r.data);
        setForm({
          name: r.data.user.name || "",
          role: r.data.user.role || "user",
          tier: r.data.user.tier || "free",
          disabled: !!r.data.user.disabled,
          admin_notes: r.data.user.admin_notes || "",
          monthly_llm_cost_cap_usd: r.data.user.monthly_llm_cost_cap_usd ?? "",
        });
      })
      .catch(() => toast.error("Failed to load user"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user_id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.monthly_llm_cost_cap_usd === "" || payload.monthly_llm_cost_cap_usd == null) {
        payload.monthly_llm_cost_cap_usd = null;
      } else {
        payload.monthly_llm_cost_cap_usd = parseFloat(payload.monthly_llm_cost_cap_usd);
      }
      await api.put(`/admin/users/${user_id}`, payload);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const impersonate = async () => {
    try {
      const r = await api.post(`/admin/users/${user_id}/impersonate`);
      startImpersonation(r.data.token, {
        target: r.data.target,
        impersonator: r.data.impersonator,
      });
      toast.success(`Now impersonating ${r.data.target.email}`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset password for ${data.user.email}? Temp password shown once.`)) return;
    try {
      const r = await api.post(`/admin/users/${user_id}/reset-password`);
      window.prompt("Copy this temp password NOW:", r.data.temp_password);
    } catch (e) { toast.error("Reset failed"); }
  };

  const softDelete = async () => {
    if (!window.confirm(`Soft-delete (disable) ${data.user.email}?`)) return;
    try {
      await api.delete(`/admin/users/${user_id}`);
      toast.success("User disabled");
      navigate("/admin/users");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 text-[#9ca3af] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading user…
      </div>
    );
  }

  const u = data.user;
  return (
    <div className="space-y-4" data-testid="admin-user-detail-page">
      <button onClick={() => navigate("/admin/users")}
              className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af] hover:text-[#e5e7eb] flex items-center gap-1"
              data-testid="back-to-users">
        <ArrowLeft className="w-3 h-3" /> All users
      </button>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-2xl tracking-tighter">{u.email}</h1>
          <div className="vx-label mt-1">{u.id}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={impersonate} disabled={u.disabled}
                  className="bg-[#fbbf24] text-black hover:bg-[#f59e0b] text-xs h-8"
                  data-testid="detail-impersonate-btn">
            <Shield className="w-3 h-3 mr-1" /> Impersonate
          </Button>
        </div>
      </header>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Accounts", value: data.accounts.length, color: "#3b82f6", testId: "stat-accounts" },
          { label: "Signals", value: data.stats.signals_count, color: "#a78bfa", testId: "stat-signals" },
          { label: "Trades", value: data.stats.journal_count, color: "#10b981", testId: "stat-trades" },
          { label: "Total P&L", value: `$${data.stats.total_pnl_usd.toFixed(2)}`,
            color: data.stats.total_pnl_usd >= 0 ? "#10b981" : "#ef4444", testId: "stat-pnl" },
          { label: "LLM cost (30d)", value: `$${data.stats.llm_cost_30d.toFixed(4)}`, color: "#f59e0b", testId: "stat-llm" },
        ].map((s) => (
          <div key={s.label} className="rounded border border-[#1f2937] bg-[#111827] p-3" data-testid={s.testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af]">{s.label}</div>
            <div className="text-lg font-mono font-black mt-1" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Editable form */}
      <section className="rounded-lg border border-[#1f2937] bg-[#111827] p-5 space-y-3">
        <div className="text-sm font-semibold">Profile</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="vx-label mb-1">Name</div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs" data-testid="input-name" />
          </div>
          <div>
            <div className="vx-label mb-1">Role</div>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full bg-[#0a0e1a] border border-[#1f2937] rounded h-9 px-2 font-mono text-xs"
                    data-testid="input-role">
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div>
            <div className="vx-label mb-1">Tier</div>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
                    className="w-full bg-[#0a0e1a] border border-[#1f2937] rounded h-9 px-2 font-mono text-xs"
                    data-testid="input-tier">
              <option value="free">free</option>
              <option value="pro">pro</option>
              <option value="pro_plus">pro_plus</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="vx-label mb-1">Monthly LLM cost cap (USD, blank = no limit)</div>
            <Input type="number" step="0.01" min="0" value={form.monthly_llm_cost_cap_usd}
                   onChange={(e) => setForm({ ...form, monthly_llm_cost_cap_usd: e.target.value })}
                   placeholder="e.g. 5.00"
                   className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs" data-testid="input-llm-cap" />
          </div>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={form.disabled}
                   onChange={(e) => setForm({ ...form, disabled: e.target.checked })}
                   data-testid="input-disabled" />
            <span className="text-xs font-mono">Account disabled</span>
          </label>
        </div>
        <div>
          <div className="vx-label mb-1">Admin notes (internal)</div>
          <Textarea rows={3} value={form.admin_notes}
                    onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                    className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs"
                    placeholder="e.g. requested refund on 2026-08-01"
                    data-testid="input-notes" />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving}
                  className="bg-[#10b981] hover:bg-[#059669] text-black text-xs h-8"
                  data-testid="save-user-btn">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save changes
          </Button>
        </div>
      </section>

      {/* Accounts table */}
      <section className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="detail-accounts-card">
        <div className="px-4 py-3 border-b border-[#1f2937] font-semibold text-sm flex items-center justify-between">
          <span>Accounts ({data.accounts.length})</span>
          <Button size="sm" variant="outline" onClick={() => navigate("/accounts")}
                  className="border-[#3b82f6]/40 text-[#3b82f6] hover:bg-[#3b82f6]/10 text-xs h-7"
                  data-testid="admin-add-account-btn">
            + Add Account
          </Button>
        </div>
        <div className="p-2">
          {data.accounts.length === 0 ? (
            <div className="text-xs text-[#6b7280] p-4 font-mono">
              No accounts. Click <b>+ Add Account</b> to create one for this user
              (goes to the full <code>/accounts</code> admin page — pick this user in the account picker).
            </div>
          ) : (
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-[#6b7280]"><th className="text-left px-3 py-1">Name</th>
                <th className="text-left px-3 py-1">Broker</th><th className="text-left px-3 py-1">Currency</th>
                <th className="text-left px-3 py-1">Strategy</th><th className="text-left px-3 py-1">Auto-Exec</th>
                <th className="text-right px-3 py-1">Actions</th></tr>
              </thead>
              <tbody>
                {data.accounts.map((a) => (
                  <tr key={a.id} className="border-t border-[#1f2937]" data-testid={`admin-acct-row-${a.id}`}>
                    <td className="px-3 py-1.5 text-[#e5e7eb]">
                      {a.account_name}
                      {a.is_default && <span className="ml-2 text-[9px] text-[#10b981] border border-[#10b981]/40 rounded px-1">default</span>}
                    </td>
                    <td className="px-3 py-1.5 uppercase text-[#3b82f6]">{a.broker_id}</td>
                    <td className="px-3 py-1.5">{a.currency}</td>
                    <td className="px-3 py-1.5">{a.active_strategy_profile_id}</td>
                    <td className="px-3 py-1.5">{a.auto_execute_enabled ? "✓ on" : "off"}</td>
                    <td className="px-3 py-1.5 text-right space-x-1 whitespace-nowrap">
                      <button
                        onClick={() => setEaSetupAcct(a)}
                        className="p-1 rounded hover:bg-[#1f2937] text-[#3b82f6]" title="EA setup + download"
                        data-testid={`ea-setup-btn-${a.id}`}
                      >
                        <Terminal className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <EASetupSlideOver
        account={eaSetupAcct}
        onClose={() => setEaSetupAcct(null)}
        onRegenerated={(newAcct) => {
          setEaSetupAcct(newAcct);
          load();  // refresh whole detail
        }}
      />

      {/* Danger zone */}
      <section className="rounded-lg border border-[#ef4444]/30 bg-gradient-to-br from-[#ef4444]/5 to-transparent p-4">
        <div className="text-sm font-semibold text-[#ef4444] mb-2">Danger zone</div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={resetPassword}
                  className="border-[#a78bfa]/40 text-[#a78bfa] hover:bg-[#a78bfa]/10 text-xs h-8"
                  data-testid="danger-reset-pw">
            <KeyRound className="w-3 h-3 mr-1" /> Reset password
          </Button>
          <Button variant="outline" size="sm" onClick={softDelete}
                  className="border-[#ef4444]/40 text-[#ef4444] hover:bg-[#ef4444]/10 text-xs h-8"
                  data-testid="danger-soft-delete">
            <Ban className="w-3 h-3 mr-1" /> Disable account
          </Button>
        </div>
      </section>
    </div>
  );
}
